import "./extraction-history-grid-modal.scss";

import { useMemo, useRef, useState } from "react";
import { ExtractSpecCardItem } from "../model";
import { AgGridReact } from "ag-grid-react";
import { Modal } from "antd";
import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import CiqAgSearch from "components/ciq-ag-search";
import { CloseOutlined } from "@ant-design/icons";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import {
  dummyColumnDef,
  truncatedTextCellRenderer
} from "components/cell-renders";
import {
  extractionHistoryStatusCellRenderer,
  extractionHistoryUploadedOnCellRenderer
} from "./cell-renderer";

type Props = {
  open: boolean;
  onClose: () => void;
  extractionHistory: Array<ExtractSpecCardItem>;
};

function ExtractionHistoryGridModal({
  open,
  onClose,
  extractionHistory
}: Props) {
  const gridRef = useRef<AgGridReact<ExtractSpecCardItem>>(null);
  const [isGridReady, setIsGridReady] = useState(false);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      getQuickFilterText: () => "",
      cellRenderer: truncatedTextCellRenderer
    } as ColDef<ExtractSpecCardItem>;
  }, []);

  const columnDefs: ColDef<ExtractSpecCardItem>[] = useMemo(() => {
    return [
      dummyColumnDef,
      {
        field: "file_name",
        headerName: "File Name",
        headerTooltip: "File Name",
        flex: 1,
        width: 232,
        getQuickFilterText: ({ value }) => value
      },
      {
        field: "total_pages",
        headerName: "Pages",
        headerTooltip: "Pages",
        width: 80
      },
      {
        field: "total_schedule_tables",
        headerName: "Tables",
        headerTooltip: "Tables",
        width: 70
      },
      {
        field: "total_materials",
        headerName: "Materials Extracted",
        headerTooltip: "Materials Extracted",
        width: 90
      },
      {
        field: "file_size",
        headerName: "File Size",
        headerTooltip: "File Size",
        width: 90,
        valueFormatter: (p: ValueFormatterParams<ExtractSpecCardItem>) =>
          p.data?.file_size ?? "-"
      },
      {
        field: "created_by_email",
        headerName: "Uploaded By",
        headerTooltip: "Uploaded By",
        minWidth: 160,
        flex: 1,
        valueFormatter: (p: ValueFormatterParams<ExtractSpecCardItem>) =>
          p.data?.created_by_email?.trim() || "-"
      },
      {
        field: "created_at",
        headerName: "Uploaded On",
        headerTooltip: "Uploaded On",
        width: 130,
        sort: "desc",
        cellRenderer: extractionHistoryUploadedOnCellRenderer
      },
      {
        field: "status",
        headerName: "Status",
        headerTooltip: "Status",
        width: 150,
        cellRenderer: extractionHistoryStatusCellRenderer
      }
    ];
  }, []);

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Extraction History",
          totalCount: extractionHistory?.length,
          filterStats: statsData
        },
        () => {}
      ),
    [extractionHistory?.length, statsData]
  );

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  return (
    <Modal
      className="custom-drawer"
      width={1040}
      footer={null}
      open={open}
      onCancel={onClose}
      destroyOnHidden
      data-testid="material-history-sidebar"
      styles={{ body: { padding: 0 } }}
      closable={false}
    >
      <div className="h-16 flex items-center justify-between px-6">
        <div className="flex items-center space-x-3">
          <div className="text-base font-semibold text-black text-opacity-90">
            Extraction History
          </div>
          {statusBudge}
        </div>
        <div className="flex items-center space-x-3">
          <CiqAgSearch
            key="Extraction_History_search"
            gridRef={gridRef}
            placeholder="Search File Name"
            disabled={!isGridReady}
          />
          <CloseOutlined
            className="cursor-pointer text-black text-opacity-45"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="h-[539px] w-full flex flex-col extraction-history-grid-modal overflow-auto">
        <AgGridReact<ExtractSpecCardItem>
          ref={gridRef}
          className="ag-theme-alpine"
          rowData={extractionHistory}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={48}
          rowHeight={42}
          onGridReady={() => {
            setTimeout(() => {
              setIsGridReady(true);
            }, 1000);
          }}
          onFilterChanged={onFiltersApplied}
        />
      </div>
    </Modal>
  );
}

export default ExtractionHistoryGridModal;
